/**
 * React Query hooks for workspace collaborators.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getWorkspaceCollaborators,
  removeWorkspaceCollaborator,
  getWorkspaceCollaboratorDetail,
  updateWorkspaceCollaborator,
  getWorkspaceMemberLabels,
  setWorkspaceMemberLabel,
  createInvite,
  createAgentKey,
  createAgentMember,
  listAgentKeys,
  revokeAgentKey,
  listInvites,
  revokeInvite,
  type AgentKeyCreated,
  type AgentKeyInfo,
  type AgentMember,
  type AgentRole,
  type CreateInviteRequest,
  type CreateInviteResponse,
  type PendingInvite,
  type WorkspaceCollaborator,
  type WorkspaceCollaboratorsResponse,
  type UnifiedCollaboratorsResponse,
  type CollaboratorDetailResponse,
  type WorkspaceCollaboratorRole,
  type MemberLabel,
  type MemberLabelFields,
} from "../services/naraDoor";
import { useWorkspace } from "../contexts/WorkspaceContext";
import { useProfile } from "../contexts/ProfileContext";

export function useWorkspaceCollaborators(workspaceId?: string) {
  const { activeWorkspace } = useWorkspace();
  const wsId = workspaceId || activeWorkspace?.id;

  return useQuery<WorkspaceCollaboratorsResponse>({
    queryKey: ["workspaceCollaborators", wsId],
    queryFn: () => {
      if (!wsId) {
        return Promise.resolve({ collaborators: [], total: 0 });
      }
      return getWorkspaceCollaborators(wsId);
    },
    enabled: !!wsId,
  });
}

export function useRemoveWorkspaceCollaborator() {
  const queryClient = useQueryClient();
  const { activeWorkspace } = useWorkspace();

  return useMutation({
    mutationFn: ({
      workspaceId,
      userId,
    }: {
      workspaceId?: string;
      userId: string;
    }) => {
      const wsId = workspaceId || activeWorkspace?.id;
      if (!wsId) {
        throw new Error("No workspace specified");
      }
      return removeWorkspaceCollaborator(wsId, userId);
    },
    onSuccess: (_, variables) => {
      const wsId = variables.workspaceId || activeWorkspace?.id;
      queryClient.invalidateQueries({
        queryKey: ["workspaceCollaborators", wsId],
      });
    },
  });
}

export function useWorkspaceCollaboratorDetail(
  workspaceId?: string,
  userId?: string
) {
  const { activeWorkspace } = useWorkspace();
  const wsId = workspaceId || activeWorkspace?.id;

  return useQuery<CollaboratorDetailResponse>({
    queryKey: ["workspaceCollaborators", wsId, "detail", userId],
    queryFn: () => {
      if (!wsId || !userId) {
        throw new Error("workspaceId and userId are required");
      }
      return getWorkspaceCollaboratorDetail(wsId, userId);
    },
    enabled: !!wsId && !!userId,
  });
}

export function useUpdateWorkspaceCollaborator() {
  const queryClient = useQueryClient();
  const { activeWorkspace } = useWorkspace();

  return useMutation({
    mutationFn: ({
      workspaceId,
      userId,
      role,
      permissions,
    }: {
      workspaceId?: string;
      userId: string;
      role?: WorkspaceCollaboratorRole;
      permissions?: string[] | null;
    }) => {
      const wsId = workspaceId || activeWorkspace?.id;
      if (!wsId) {
        throw new Error("No workspace specified");
      }
      return updateWorkspaceCollaborator(wsId, userId, { role, permissions });
    },
    onSuccess: (_, variables) => {
      const wsId = variables.workspaceId || activeWorkspace?.id;
      // Invalidate both the list and detail queries
      queryClient.invalidateQueries({
        queryKey: ["workspaceCollaborators", wsId],
      });
      queryClient.invalidateQueries({
        queryKey: ["workspaceCollaborators", wsId, "detail", variables.userId],
      });
    },
  });
}

/** The signed-in user's role in the active workspace, or null without an active seat. */
export function useMyWorkspaceRole(): string | null {
  const { profile } = useProfile();
  const { data } = useWorkspaceCollaborators();
  const seat = data?.collaborators.find((c) => c.user_id === profile?.id);
  return seat?.status === "active" ? seat.role : null;
}

/** True when the signed-in user is an active owner/admin of the active workspace. */
export function useIsWorkspaceAdmin(): boolean {
  const role = useMyWorkspaceRole();
  return role === "owner" || role === "admin";
}

/** Admin-private labels of the workspace, keyed by user id. Pass enabled=false for a non-admin. */
export function useWorkspaceMemberLabels(workspaceId: string | undefined, enabled: boolean) {
  return useQuery<Record<string, MemberLabel>>({
    queryKey: ["workspaceMemberLabels", workspaceId],
    queryFn: async () => {
      try {
        return Object.fromEntries((await getWorkspaceMemberLabels(workspaceId!)).labels.map((l) => [l.user_id, l]));
      } catch (err) {
        console.error("getWorkspaceMemberLabels failed", { workspaceId, error: (err as Error).message });
        throw err;
      }
    },
    enabled: enabled && !!workspaceId,
  });
}

export function useSetWorkspaceMemberLabel() {
  const queryClient = useQueryClient();

  return useMutation<MemberLabel, Error, { workspaceId: string; userId: string; label: MemberLabelFields }>({
    mutationFn: ({ workspaceId, userId, label }) => setWorkspaceMemberLabel(workspaceId, userId, label),
    onSuccess: (_, { workspaceId }) => {
      queryClient.invalidateQueries({ queryKey: ["workspaceMemberLabels", workspaceId] });
    },
  });
}

export function useWorkspaceInvites(workspaceId?: string) {
  return useQuery<PendingInvite[]>({
    queryKey: ["workspaceInvites", workspaceId],
    queryFn: async () => (await listInvites(workspaceId!)).invites,
    enabled: !!workspaceId,
    // listInvites is admin-only: a refusal means "not for you", not a failure worth retrying.
    retry: false,
  });
}

export function useCreateInvite() {
  const queryClient = useQueryClient();

  return useMutation<CreateInviteResponse, Error, CreateInviteRequest>({
    mutationFn: createInvite,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["workspaceInvites", variables.workspaceId] });
    },
  });
}

export function useRevokeInvite(workspaceId: string) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (inviteId) => revokeInvite(workspaceId, inviteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workspaceInvites", workspaceId] });
    },
  });
}

/** The keys of an AI agent, newest first. Admin-only on the server: pass enabled=false for anyone else. */
export function useAgentKeys(workspaceId: string | undefined, userId: string | undefined, enabled: boolean) {
  return useQuery<AgentKeyInfo[]>({
    queryKey: ["agentKeys", workspaceId, userId],
    queryFn: async () => {
      try {
        return (await listAgentKeys(workspaceId!, userId!)).keys;
      } catch (err) {
        console.error("listAgentKeys failed", { workspaceId, userId, code: (err as { code?: string }).code });
        throw err;
      }
    },
    enabled: enabled && !!workspaceId && !!userId,
  });
}

export function useCreateAgentMember(workspaceId: string) {
  const queryClient = useQueryClient();

  return useMutation<AgentMember, Error, { display_name: string; role: AgentRole }>({
    mutationFn: (body) => createAgentMember(workspaceId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workspaceCollaborators", workspaceId] });
    },
  });
}

export function useCreateAgentKey(workspaceId: string) {
  const queryClient = useQueryClient();

  return useMutation<AgentKeyCreated, Error, string>({
    mutationFn: (userId) => createAgentKey(workspaceId, userId),
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: ["agentKeys", workspaceId, userId] });
    },
  });
}

export function useRevokeAgentKey(workspaceId: string, userId: string) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (keyId) => revokeAgentKey(workspaceId, userId, keyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agentKeys", workspaceId, userId] });
    },
  });
}
